import { T } from '@transifex/react';
import { Heading, Text, VStack } from '@vidahealth/vida-ui';
import { type ComponentType } from 'react';
import { Pressable } from 'react-native';
import type { SvgProps } from 'react-native-svg';

import AppleCalendar from '@/assets/icons/apple-calendar.svg';
import GoogleCalendar from '@/assets/icons/google-calendar.svg';
import OutlookCalendar from '@/assets/icons/outlook-calendar.svg';
import { BottomSheet } from '@/modules/ui';

import type { CalendarProvider } from '../utils/calendarLinks';

const OPTIONS: { id: CalendarProvider; label: string; Icon: ComponentType<SvgProps> }[] = [
  { id: 'apple', label: 'Apple', Icon: AppleCalendar },
  { id: 'google', label: 'Google', Icon: GoogleCalendar },
  { id: 'outlook', label: 'Microsoft Outlook', Icon: OutlookCalendar },
];

/**
 * Private to CoachMatchingSuccessScreen — the calendar-provider chooser. Picking
 * a provider opens that provider's add-event link / .ics.
 */
export function AddToCalendarSheet({
  isOpen,
  onClose,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (provider: CalendarProvider) => void;
}) {
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      stickyHeader={
        <Heading size='md' className='text-primary-800 dark:text-primary-0'>
          <T _str='Add to calendar' />
        </Heading>
      }
    >
      <VStack space='lg'>
        <Text size='sm' className='text-typography-700'>
          <T _str='Select a calendar to add your session to.' />
        </Text>
        {OPTIONS.map(({ id, label, Icon }) => (
          <Pressable
            key={id}
            accessibilityRole='button'
            accessibilityLabel={label}
            onPress={() => onSelect(id)}
            testID={`add-to-calendar-${id}`}
            className='flex-row items-center gap-4 py-3'
          >
            <Icon width={24} height={24} />
            <Text size='md' className='text-typography-900'>
              {label}
            </Text>
          </Pressable>
        ))}
      </VStack>
    </BottomSheet>
  );
}
